import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';
import { createSessionManager, startCrossTabSync } from '@elebyte/auth';
import { getCookie, deleteCookie, COOKIE_NAMES } from '../utils/cookies';

const AuthContext = createContext(null);

// Session management: inactivity timeout + cross-tab sync
const sessionManager = createSessionManager({ inactivityTimeout: 60 * 60 * 1000 }); // 60 min
const stopCrossTabSync = startCrossTabSync({
  watchKeys: ['accessToken'],
  onLogout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    deleteCookie(COOKIE_NAMES.ACCESS_TOKEN);
    deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
    window.location.reload();
  },
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.elebyte.io';

  // Check for existing session on mount (localStorage first, then cross-subdomain cookies)
  useEffect(() => {
    const checkAuth = async () => {
      let token = localStorage.getItem('accessToken');

      // If no token in localStorage, check cross-subdomain cookies set by Auth portal
      if (!token) {
        const cookieToken = getCookie(COOKIE_NAMES.ACCESS_TOKEN);
        if (cookieToken) {
          // Hydrate localStorage so the API client can use bearer auth
          localStorage.setItem('accessToken', cookieToken);
          const cookieRefresh = getCookie(COOKIE_NAMES.REFRESH_TOKEN);
          if (cookieRefresh) {
            localStorage.setItem('refreshToken', cookieRefresh);
          }
          token = cookieToken;
        }
      }

      if (token) {
        try {
          const response = await authApi.getMe();
          setUser(response.data.user || response.data);
          setIsAuthenticated(true);
        } catch (error) {
          // Token invalid, clear both localStorage and cookies
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          deleteCookie(COOKIE_NAMES.ACCESS_TOKEN);
          deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Redirect to auth login
  const login = useCallback(() => {
    const currentUrl = window.location.href;
    window.location.href = `${AUTH_URL}/login?redirect=${encodeURIComponent(currentUrl)}`;
  }, [AUTH_URL]);

  // Logout
  const logout = useCallback(() => {
    authApi.logout();
    deleteCookie(COOKIE_NAMES.ACCESS_TOKEN);
    deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Handle OAuth callback (if tokens are passed in URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refreshToken = params.get('refreshToken');

    if (token) {
      localStorage.setItem('accessToken', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      // Remove tokens from URL
      params.delete('token');
      params.delete('refreshToken');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Fetch user info
      authApi.getMe()
        .then((response) => {
          setUser(response.data.user || response.data);
          setIsAuthenticated(true);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
